import type { InspectedBuilding, SurveyV2 } from '@ghp/domain'
import { INSPECTED_BUILDINGS, INSPECTED_BUILDING_LABELS } from '@ghp/domain'
import { Card, CardBody, Checkbox, InfoNote, SectionLabel } from '@ghp/ui'
import { Building, Home, Info, Store, Warehouse } from 'lucide-react'
import { useId, type ReactNode } from 'react'

import s from './InspectedBuildings.module.css'

/** L'icône que le prototype donne à chaque bâtiment. */
const BUILDING_ICONS: Record<InspectedBuilding, ReactNode> = {
  main: <Home size={18} aria-hidden />,
  rear: <Warehouse size={18} aria-hidden />,
  front: <Store size={18} aria-hidden />,
}

/**
 * Carte « Bâtiments contrôlés » de l'onglet « Relevé » (#411) — la maison et ses deux annexes, AR
 * et AV.
 *
 * ⚠️ **La maison n'est pas cochée d'office.** Le prototype la pré-coche, mais il travaille sur une
 * fiche de démonstration déjà remplie : sur un brouillon neuf, une case cochée que personne n'a
 * touchée dirait au bureau que l'agent a contrôlé la maison avant même qu'il soit descendu de
 * voiture. Rien n'est écrit tant que l'agent n'a pas coché — même règle que la conclusion.
 */
export function InspectedBuildings({
  survey,
  disabled,
  onChange,
}: {
  survey: SurveyV2
  disabled?: boolean
  onChange?: (survey: SurveyV2) => void
}) {
  const inspected = survey.inspectedBuildings ?? []

  function toggle(building: InspectedBuilding): void {
    onChange?.({
      ...survey,
      inspectedBuildings: inspected.includes(building)
        ? inspected.filter((kept) => kept !== building)
        : [...inspected, building],
    })
  }

  return (
    <Card>
      <CardBody className={s.buildings}>
        <SectionLabel>
          <Building size={16} aria-hidden />
          Bâtiments contrôlés — Maison · AR · AV
        </SectionLabel>
        <InfoNote icon={<Info size={14} />}>
          Les bâtiments <b>AR</b> (arrière) et <b>AV</b> (avant) sont saisis, consultés et restitués
          comme la maison : anomalies, éléments techniques et surfaces leur sont rattachables.
        </InfoNote>
        <div className={s.buildingList}>
          {INSPECTED_BUILDINGS.map((building) => (
            <CheckOption
              key={building}
              icon={BUILDING_ICONS[building]}
              checked={inspected.includes(building)}
              disabled={disabled}
              onCheckedChange={() => toggle(building)}
            >
              {INSPECTED_BUILDING_LABELS[building]}
            </CheckOption>
          ))}
        </div>
      </CardBody>
    </Card>
  )
}

/** `.opt` du prototype : une rangée bordée, cochée en bleu, dont toute la surface est la cible. */
function CheckOption({
  icon,
  checked,
  disabled,
  onCheckedChange,
  children,
}: {
  icon: ReactNode
  checked: boolean
  disabled?: boolean
  onCheckedChange: () => void
  children: ReactNode
}) {
  const id = useId()

  return (
    <label className={s.option} htmlFor={id} data-on={checked ? '' : undefined}>
      <Checkbox id={id} checked={checked} disabled={disabled} onCheckedChange={onCheckedChange} />
      {icon}
      <span className={s.optionName}>{children}</span>
    </label>
  )
}
